import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BoutiqueItems } from '../types';
import API from '../lib/API';
import { setBoutiqueItems } from '../slices/boutiquesSlice';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';

interface BoutiqueItemFormModalProps {
    show: boolean;
    handleClose: () => void;
    token: string;
    boutiqueId: number;
    item?: BoutiqueItems;
}

const BoutiqueItemFormModal: React.FC<BoutiqueItemFormModalProps> = ({ 
    show, 
    handleClose, 
    token, 
    boutiqueId, 
    item 
}) => {
    const dispatch = useDispatch();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [itemPrice, setItemPrice] = useState('');
    const [itemCurrency, setItemCurrency] = useState('XOF');
    const [discount, setDiscount] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (item) {
            setName(item.name);
            setDescription(item.description || '');
            setItemPrice((item.itemPrice || 0).toString());
            setItemCurrency(item.itemCurrency || 'XOF');
            setDiscount((item.discount || 0).toString());
        } else {
            setName('');
            setDescription('');
            setItemPrice('');
            setItemCurrency('XOF');
            setDiscount('');
        }
    }, [item]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const itemData = {
                name,
                description: description || null,
                itemPrice: parseFloat(itemPrice) || 0,
                itemCurrency,
                discount: parseInt(discount) || 0,
                boutiqueId
            };

            if (item) {
                await API.updateBoutiqueItem(token, item.boutiqueItemId, {
                    ...itemData,
                    images: item.images,
                });
            } else {
                await API.createBoutiqueItem(token, itemData);
            }

            const updatedItems = await API.getBoutiqueItems(token, boutiqueId);
            dispatch(setBoutiqueItems(updatedItems));
            setName('');
            setDescription('');
            setItemPrice('');
            setItemCurrency('XOF');
            setDiscount('');
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!item) return;

        setLoading(true);
        setError('');

        try {
            await API.deleteBoutiqueItem(token, item.boutiqueItemId);
            const updatedItems = await API.getBoutiqueItems(token, boutiqueId);
            dispatch(setBoutiqueItems(updatedItems));
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{item ? 'Edit Item' : 'New Item'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description of the item"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Price</Form.Label>
                        <div className="d-flex">
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={itemPrice}
                                onChange={(e) => setItemPrice(e.target.value)}
                                placeholder="0.00"
                                required
                            />
                            <Form.Select
                                value={itemCurrency}
                                onChange={(e) => setItemCurrency(e.target.value)}
                                style={{ width: 'auto', marginLeft: '10px' }}
                            >
                                <option value="XOF">XOF</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                            </Form.Select>
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Discount (%)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            value={discount}
                            onChange={(e) => setDiscount(e.target.value)}
                            placeholder="0"
                        />
                        <Form.Text className="text-muted">
                            Discount percentage (0-100)
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {item && (
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={loading}
                        className="me-auto"
                    >
                        Delete
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default BoutiqueItemFormModal; 